/**
 * Générateur — Automatisme : comparer des aires sans mesurer
 * Attendu ID : 1025 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait comparer des
 * aires sans avoir recours à la mesure, par superposition ou par découpage
 * et recollement de surfaces. »
 * Labels (cartes exerciseur) :
 *   - Comparer des aires sans mesurer
 *
 * ── Plan de cartes (2026-08-21, cadrage prof) ──────────────────────────────
 * UN SEUL format (décision prof : pas de formats de remplissage) : le DUEL
 * de figures. Deux figures A et B côte à côte, QCM à 3 choix (A plus
 * grande / B plus grande / même aire). Les paires sont PROUVABLES par le
 * procédé visé, jamais par une formule :
 *   - rectangle vs parallélogramme de même base et même hauteur (le
 *     triangle se découpe et se recolle : MÊME aire) — l'illustration
 *     canonique du cadrage ;
 *   - rectangle vs parallélogramme de hauteur différente (l'une déborde) ;
 *   - rectangle vs le même rectangle TOURNÉ (superposition : même aire) ;
 *   - deux demi-rectangles (triangles) en miroir ou de hauteurs
 *     différentes ;
 *   - rectangle vs sa moitié en triangle (différence évidente).
 * SANS MESURE : aucune cote affichée ; le quadrillage est PÂLE et en
 * pointillé (il guide le découpage) et les parallélogrammes / triangles
 * imposent des demi-carreaux — compter est malcommode, découper est
 * naturel (réserve du cadrage intégrée). Item « périmètre vs aire » écarté
 * (hors périmètre de l'automatisme).
 * Aires calculées par SHOELACE en demi-carreaux entiers (jamais de
 * flottant), vérité = comparaison de ces entiers, garde qui throw.
 */
import {
    randomChoice, randInt,
    makeQcm,
} from '../../../utils';

const ATTENDU_ID = 1025;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Comparer des aires sans mesurer';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1025 : ${message}`);
};

// ─── Géométrie en carreaux (aire = shoelace, en demi-carreaux entiers) ────

/** Aire × 2 (demi-carreaux) d'un polygone en unités de grille — entier. */
const aire2 = (pts) => {
    let somme = 0;
    for (let k = 0; k < pts.length; k++) {
        const [x1, y1] = pts[k];
        const [x2, y2] = pts[(k + 1) % pts.length];
        somme += x1 * y2 - x2 * y1;
    }
    garde(Number.isInteger(somme), 'aire non entière en demi-carreaux');
    return Math.abs(somme);
};

/** Formes (unités de grille, y vers le bas) + leur description accessible. */
const rect = (L, h) => ({
    pts: [[0, 0], [L, 0], [L, h], [0, h]],
    desc: `un rectangle de ${L} carreaux sur ${h}`,
});
const para = (L, h, d) => {
    // le décalage DOIT être un nombre entier de carreaux (cadrage prof
    // 2026-08-21) : la coupe tombe sur une ligne du quadrillage et le
    // triangle se recolle sans reste
    garde(Number.isInteger(d) && d >= 1, 'décalage non entier');
    return {
        pts: [[d, 0], [d + L, 0], [L, h], [0, h]],
        desc: `un parallélogramme de base ${L} carreaux et de hauteur ${h}`,
    };
};
const triangle = (L, h, miroir) => ({
    pts: miroir ? [[L, 0], [L, h], [0, h]] : [[0, 0], [L, h], [0, h]],
    desc: `un triangle, moitié d'un rectangle de ${L} carreaux sur ${h}`,
});

/** Le catalogue des paires (chaque procédé du programme est représenté). */
const PAIRES = [
    () => {                                            // découpage-recollement
        const L = randInt(3, 5); const h = randInt(2, 4);
        return [rect(L, h), para(L, h, randInt(1, 2))];
    },
    () => {                                            // hauteurs différentes
        const L = randInt(3, 5); const h = randInt(2, 3);
        return [rect(L, h), para(L, h + 1, randInt(1, 2))];
    },
    () => {                                            // superposition (rotation)
        const L = randInt(3, 5); const h = randInt(2, 4);
        garde(L !== h || L < 5, 'dimensions');
        return L === h ? [rect(L, h - 1), rect(h - 1, L)] : [rect(L, h), rect(h, L)];
    },
    () => {                                            // triangles en miroir
        const L = randInt(3, 5); const h = randInt(2, 4);
        return [triangle(L, h, false), triangle(L, h, true)];
    },
    () => {                                            // triangles de hauteurs différentes
        const L = randInt(3, 5); const h = randInt(2, 3);
        return [triangle(L, h, false), triangle(L, h + 1, randomChoice([true, false]))];
    },
    () => {                                            // le rectangle et sa moitié
        const L = randInt(3, 5); const h = randInt(2, 4);
        return [rect(L, h), triangle(L, h, randomChoice([true, false]))];
    },
];

// ─── Le dessin : deux zones quadrillées pâles, figures sans aucune cote ───

const U = 22;
const GRILLE = '#94a3b8';
const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const dessinerFigure = (forme, x0, y0, couleur) => {
    const xs = forme.pts.map((p) => p[0]);
    const ys = forme.pts.map((p) => p[1]);
    const wU = Math.max(...xs); const hU = Math.max(...ys);
    garde(Math.min(...xs) >= 0 && Math.min(...ys) >= 0, 'forme non normalisée');
    let svg = '';
    // quadrillage pâle en pointillé, limité à la boîte de la figure (+1)
    for (let gx = 0; gx <= wU; gx++) {
        svg += `<line x1="${x0 + gx * U}" y1="${y0}" x2="${x0 + gx * U}" y2="${y0 + hU * U}" stroke="${GRILLE}" stroke-width="1"/>`;
    }
    for (let gy = 0; gy <= hU; gy++) {
        svg += `<line x1="${x0}" y1="${y0 + gy * U}" x2="${x0 + wU * U}" y2="${y0 + gy * U}" stroke="${GRILLE}" stroke-width="1"/>`;
    }
    svg += `<polygon points="${forme.pts.map(([x, y]) => `${x0 + x * U},${y0 + y * U}`).join(' ')}"`
        + ` fill="${couleur}" fill-opacity="0.45" stroke="${couleur}" stroke-width="2.6"/>`;
    return { svg, wPx: wU * U, hPx: hU * U };
};

const dessinerDuel = (fA, fB) => {
    const A = dessinerFigure(fA, 0, 0, '#1E3A8A');
    const B = dessinerFigure(fB, 0, 0, '#0d9488');
    const H = Math.max(A.hPx, B.hPx);
    const wTotal = A.wPx + B.wPx + 58;
    garde(wTotal <= 330 && H <= 130, 'duel trop grand');
    const xA = (340 - wTotal) / 2 + 5;
    const xB = xA + A.wPx + 58;
    const yA = 52 + (H - A.hPx) / 2;
    const yB = 52 + (H - B.hPx) / 2;
    const rA = dessinerFigure(fA, xA, yA, '#1E3A8A');
    const rB = dessinerFigure(fB, xB, yB, '#0d9488');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 ${H + 76}" width="340" role="img"`
        + ` aria-label="Figure A : ${fA.desc}. Figure B : ${fB.desc}. Le quadrillage aide au découpage, aucune mesure n'est donnée">`
        + `<text x="${xA + rA.wPx / 2}" y="24" font-size="17" font-weight="800" fill="#1E3A8A" text-anchor="middle" font-family="system-ui,sans-serif">A</text>`
        + `<text x="${xB + rB.wPx / 2}" y="24" font-size="17" font-weight="800" fill="#0d9488" text-anchor="middle" font-family="system-ui,sans-serif">B</text>`
        + rA.svg + rB.svg
        + '</svg>';
    return dataUrl(svg);
};

// ─── Le format unique : le duel ───────────────────────────────────────────

const CHOIX = [
    'A a la plus grande aire',
    'B a la plus grande aire',
    'A et B ont la même aire',
];

const genComparerQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        let [fA, fB] = randomChoice(PAIRES)();
        if (Math.random() < 0.5) [fA, fB] = [fB, fA];   // A/B équilibrés
        const a2A = aire2(fA.pts);
        const a2B = aire2(fB.pts);
        const bonne = a2A === a2B ? CHOIX[2] : (a2A > a2B ? CHOIX[0] : CHOIX[1]);
        const payload = makeQcm(
            [...CHOIX],
            bonne,
            '<p><b>Compare les aires des deux figures, sans mesurer : le quadrillage sert à découper et recoller.</b></p>{{element:0}}',
            { ...opts(L1), elements_additionnels: elImage(dessinerDuel(fA, fB)) },
        );
        payload._meta.params = { descA: fA.desc, descB: fB.desc, a2A, a2B };
        return payload;
    }
    garde(false, 'duel introuvable');
    return null;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'UN SEUL format (décision prof) : le duel A/B, 3 choix fixes — les « distracteurs » sont les deux autres verdicts, jamais des inventions',
        'aucune cote affichée (comparer SANS mesurer) ; quadrillage pâle pointillé + demi-carreaux : compter est malcommode, découper est naturel',
        'aires en demi-carreaux ENTIERS (shoelace), vérité = comparaison de ces entiers, garde qui throw',
    ],
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    aires_comparer_qcm: genComparerQcm,
};

export const CATEGORIES = [
    {
        id: 'airescomp',
        label: L1,
        formatsList: [{ key: 'aires_comparer_qcm', label: 'Duel' }],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else key = 'aires_comparer_qcm';
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
